import React, { useState, useEffect } from "react";
import { Box } from "@material-ui/core";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { ColumnsFilterPagesEnum } from "../../../Enums";
import { useNewLayoutShared } from "../../../Contexts/NewLayoutSharedContext";
import {
  GetRotationSchemeByIdServices,
  UpdateRotationScheme,
  OrganizationUserSearch,
  GetAllBranches,
  GetTeam,
} from "../../../Services";
import { useVerticalNav } from "../../../Contexts/VerticalNavContext";
import { buildGetRotationQueryBody } from "./util";
import { RotationSchemaPermissions } from "../../../Permissions";
import {
  CustomPermission,
  PageTitle,
  CustomButton,
  CustomBreadCrumb,
  CustomBadge,
  CustomIconButton,
  RolePinSideTab,
  SaveFilterModal,
  CustomAsyncAutocomplete,
  ReskinningInputs,
  SortingDropdown,
} from "../../../Components";
import RotationTable from "./RotationTable/RotationTable";
import { RotationTableData } from "./RotationTable/RotationTableHeaderData";

import { useSelectedTheme, useTranslate } from "../../../Hooks";

import useStyles from "./styles";

import { ColumnsThree, PlusIcon, SettingsFour } from "../../../assets/icons";
import { useDebouncedAction } from "../../../Hooks/DebouncedAction";

function NewAgentsRotationCriteriaView() {
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const [showFiltersSection, setShowFiltersSection] = useState(true);
  const [isPinTabOpen, setIsPinTabOpen] = useState(false);
  const navigate = useHistory();
  const { filterModalData, savedFiltersCriteria } = useNewLayoutShared();
  const { isDarkMode } = useSelector((state) => state.theme);
  const { setAlertBoxContent } = useVerticalNav();
  const [orderByOptions, setOrderByOptions] = useState({
    filterBy: null,
    orderBy: null,
  });
  const [filters, setFilters] = useState({
    search: "",
    agentId: "",
    teamIds: [],
    branchIds: [],
  });

  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();

  const handlePinClose = () => {
    setIsPinTabOpen(!isPinTabOpen);
  };
  const onSchemaSearch = useDebouncedAction((value) => {
    setFilters({ ...filters, search: value });
  });

  const getSchemaById = async (rotationSchemeId) => {
    const res = await GetRotationSchemeByIdServices(rotationSchemeId);
    if (!(res && res.status && res.status !== 200)) return res;
  };
  const onStatusChangeHandler = async (schemaData, onError) => {
    const schema = await getSchemaById(schemaData.rotationSchemeId);
    const body = buildGetRotationQueryBody(schemaData, schema);
    const res = await UpdateRotationScheme(schemaData.rotationSchemeId, {
      ...body,
      isActive: !schemaData.isActive,
    });
    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate(`schema-status-changed`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } else {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate(`failed-to-change-schema-status`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      onError();
    }
  };

  const [updateSelectedColumnItems, setUpdateSelectedColumnItems] = useState(
    RotationTableData()
  );

  const allFormFields = RotationTableData(
    isDarkMode,
    onStatusChangeHandler
  ).map((column) => ({
    ...column,
  }));

  return (
    <>
      <CustomBreadCrumb
        breadcrumbs={[
          {
            label: translate("agent-management"),
            nonClickable: true,
          },
        ]}
        containerClasses={styles.breadCrumbContainer}
      >
        <CustomBadge
          Style={{
            padding: "4px 8px",
            borderRadius: "6px",
            lineHeight: "20px",
          }}
          label={translate("rotation-schema")}
          BackgroundColor={palette.breadcrumbs.brand_bg_hover}
          BorderColor={palette.breadcrumbs.brand_bg_hover}
          Color={palette.breadcrumbs.brand_fg_hover}
        />
      </CustomBreadCrumb>
      <PageTitle
        title={translate("agent-rotation-page-title")}
        subTitle={translate("agent-rotation-page-subtitle")}
        hideDivider
      >
        <CustomPermission
          permissionsList={Object.values(RotationSchemaPermissions)}
          permissionsId={
            RotationSchemaPermissions.CreateNewRotations.permissionsId
          }
        >
          <CustomButton
            boxShadow="xs"
            size="lg"
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate.push("/home/rotation-criteria/add-criteria");
            }}
            style={{ width: "133px" }}
            startIcon={
              <PlusIcon
                width="20"
                height="20"
                fill={palette.button.primary_fg}
              />
            }
          >
            {translate("add-new")}
          </CustomButton>
        </CustomPermission>
      </PageTitle>
      <Box className={styles.tableContainer}>
        <Box className={styles.filtersButtons}>
          <CustomIconButton
            variant="outlined"
            size="md"
            boxShadow="none"
            color="secondary"
            onClick={() => setShowFiltersSection((prev) => !prev)}
          >
            <SettingsFour
              width="20"
              height="20"
              fill={palette.button.secondary_fg}
            />
          </CustomIconButton>
          <CustomIconButton
            variant="outlined"
            size="md"
            boxShadow="none"
            color="secondary"
            onClick={() => setIsPinTabOpen((prev) => !prev)}
          >
            <ColumnsThree
              width="20"
              height="20"
              fill={palette.button.secondary_fg}
            />
          </CustomIconButton>
          <RolePinSideTab
            setUpdateSelectedColumnItems={setUpdateSelectedColumnItems}
            open={isPinTabOpen}
            onClose={handlePinClose}
            allFormFields={allFormFields}
            selectAllByDefault
            type={ColumnsFilterPagesEnum.rotationTable.key}
          />
        </Box>
        {showFiltersSection && (
          <Box className={styles.filtersContainer}>
            <CustomAsyncAutocomplete
              rootCustomClasses={styles.inputwrapper}
              className={styles.autoCompleteInput}
              onChange={(_e, value) => {
                setFilters({ ...filters, agentId: value.id });
              }}
              mapper={(data) => {
                return data.map((item) => item);
              }}
              queryKey={"search"}
              placeholder={translate("Agent")}
              onFetchData={(filter) => OrganizationUserSearch({})}
              filter={{ search: null }}
              displayLabel={(option) => option?.fullName ?? ""}
              renderOption={(option) => option?.fullName}
              chipsLabel={(option) => {
                return option?.fullName;
              }}
              getOptionSelected={(option, value) => option?.id === value?.id}
            />
            <CustomAsyncAutocomplete
              rootCustomClasses={styles.inputwrapper}
              className={styles.autoCompleteInput}
              placeholder={translate("branches")}
              onFetchData={GetAllBranches}
              chipsLabel={(option) => option.branchName || ""}
              displayLabel={(option) => option.branchName || ""}
              getOptionSelected={(option, value) =>
                option?.branchId === value?.branchId
              }
              onChange={(_e, value) => {
                setFilters({
                  ...filters,
                  branchIds: value.map((branch) => branch.branchId),
                });
              }}
              mapper={(data) => data}
              filter={{
                pageSize: 25,
                pageIndex: 1,
                filterBy: null,
                orderBy: null,
              }}
              multiple
            />
            <CustomAsyncAutocomplete
              rootCustomClasses={styles.inputwrapper}
              className={styles.autoCompleteInput}
              placeholder={translate("teams")}
              onFetchData={(filter) => GetTeam(...Object.values(filter))}
              chipsLabel={(option) => option.teamsName || option.teamName || ""}
              onChange={(_e, value) => {
                setFilters({
                  ...filters,
                  teamIds: value.map((team) => team.teamsId || team.teamId),
                });
              }}
              displayLabel={(option) =>
                option.teamsName || option.teamName || ""
              }
              getOptionSelected={(option, value) =>
                value.teamsId === option.teamsId ||
                value.teamId === option.teamsId
              }
              mapper={(data) => data}
              filter={{
                pageIndex: 1,
                pageSize: 25,
                search: "",
              }}
              multiple
            />
            <ReskinningInputs
              inputPlaceholder={translate("Rotation scheme")}
              overInputIcon
              fieldClasses={styles.searchInput}
              onInputChanged={(e) => {
                onSchemaSearch(e.target.value);
              }}
            />
            <Box style={{ width: "165px" }}>
              <SortingDropdown
                setAdvancedSearchBody={setOrderByOptions}
                translate={translate}
                hideLabel
                customRootClasses={styles.buttonSortWrapper}
              />
            </Box>
          </Box>
        )}
        <CustomPermission
          permissionsList={Object.values(RotationSchemaPermissions)}
          permissionsId={
            RotationSchemaPermissions.ViewAllRotationSchema.permissionsId
          }
        >
          <RotationTable
            filters={filters}
            SelectedColumns={updateSelectedColumnItems}
            orderByOptions={orderByOptions}
          />
        </CustomPermission>
        {(filterModalData?.isOpen || savedFiltersCriteria?.isOpen) && (
          <SaveFilterModal />
        )}
      </Box>
    </>
  );
}

export default NewAgentsRotationCriteriaView;
